<script>
import {
  createHeadAdmin,
  deleteHeadAdmin,
  getHeadAdmins,
  updateHeadAdmin,
  updateHeadAdminPassword,
} from '@/api/adminUser.js'
import { getAdminInfo } from '@/utils/token.js'

export default {
  name: 'HeadAdminListPage',
  data() {
    return {
      keyword: '',
      items: [],
      loading: false,
      dialog: false,
      passwordDialog: false,
      deleteDialog: false,
      saving: false,
      errorMessage: '',
      passwordErrorMessage: '',
      deleteErrorMessage: '',
      editingCode: '',
      deleteTarget: null,
      passwordTarget: null,
      form: this.emptyForm(),
      passwordForm: { newPassword: '' },
      adminInfo: getAdminInfo(),
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    emptyForm() {
      return {
        adminName: '',
        adminEmail: '',
        adminPassword: '',
        adminPosition: '',
        adminPhone: '',
      }
    },
    formatDate(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 16)
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await getHeadAdmins({ keyword: this.keyword || undefined })
        if (res.data?.code === 0) {
          this.items = res.data.items || []
          return
        }
        this.errorMessage = res.data?.message || '본사관리자 목록을 불러오지 못했습니다.'
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editingCode = ''
      this.form = this.emptyForm()
      this.errorMessage = ''
      this.dialog = true
    },
    openEdit(item) {
      this.editingCode = item.adminUserCode
      this.form = {
        adminName: item.adminName || '',
        adminEmail: item.adminEmail || '',
        adminPassword: '',
        adminPosition: item.adminPosition || '',
        adminPhone: item.adminPhone || '',
      }
      this.errorMessage = ''
      this.dialog = true
    },
    validateForm() {
      if (!this.form.adminName?.trim()) return '관리자 이름을 입력해 주세요.'
      if (!this.form.adminEmail?.trim()) return '이메일을 입력해 주세요.'
      if (!this.editingCode && !this.form.adminPassword?.trim()) return '초기 비밀번호를 입력해 주세요.'
      if (!this.editingCode && this.form.adminPassword.length < 8) return '비밀번호는 8자 이상이어야 합니다.'
      return ''
    },
    async save() {
      this.errorMessage = this.validateForm()
      if (this.errorMessage) return

      this.saving = true
      try {
        const payload = {
          adminName: this.form.adminName.trim(),
          adminEmail: this.form.adminEmail.trim(),
          adminPosition: this.form.adminPosition?.trim() || '',
          adminPhone: this.form.adminPhone?.trim() || '',
        }
        const res = this.editingCode
          ? await updateHeadAdmin(this.editingCode, payload)
          : await createHeadAdmin({ ...payload, adminPassword: this.form.adminPassword })

        if (res.data?.code === 0) {
          this.dialog = false
          await this.fetchList()
          return
        }
        this.errorMessage = res.data?.message || '저장에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    openPassword(item) {
      this.passwordTarget = item
      this.passwordForm = { newPassword: '' }
      this.passwordErrorMessage = ''
      this.passwordDialog = true
    },
    async savePassword() {
      if (!this.passwordForm.newPassword || this.passwordForm.newPassword.length < 8) {
        this.passwordErrorMessage = '새 비밀번호는 8자 이상이어야 합니다.'
        return
      }
      this.saving = true
      try {
        const res = await updateHeadAdminPassword(this.passwordTarget.adminUserCode, {
          newPassword: this.passwordForm.newPassword,
        })
        if (res.data?.code === 0) {
          this.passwordDialog = false
          return
        }
        this.passwordErrorMessage = res.data?.message || '비밀번호 변경에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    requestRemove(item) {
      this.deleteTarget = item
      this.deleteErrorMessage = ''
      this.deleteDialog = true
    },
    async remove() {
      this.saving = true
      try {
        const res = await deleteHeadAdmin(this.deleteTarget.adminUserCode)
        if (res.data?.code === 0) {
          this.deleteDialog = false
          this.deleteTarget = null
          await this.fetchList()
          return
        }
        this.deleteErrorMessage = res.data?.message || '삭제에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    isMe(item) {
      return item.adminUserCode === this.adminInfo.adminCode
    },
  },
}
</script>

<template>
  <div class="head-admin-page">
    <div class="page-header">
      <div>
        <h1>본사관리자 관리</h1>
        <p>본사 관리자 계정을 등록하고 관리합니다.</p>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">본사관리자 등록</v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="search-card">
      <v-card-title>검색 조건</v-card-title>
      <v-card-text>
        <div class="search-row">
          <v-text-field
            v-model="keyword"
            label="이름, 이메일, 연락처 검색"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            @keyup.enter="fetchList"
          />
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="keyword = ''; fetchList()">초기화</v-btn>
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="fetchList">검색</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-card-title>
        본사관리자 목록
        <v-chip size="small" class="ml-2">{{ items.length }}건</v-chip>
      </v-card-title>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table>
        <thead>
          <tr>
            <th>번호</th>
            <th>이름</th>
            <th>이메일</th>
            <th>직책</th>
            <th>연락처</th>
            <th>등록일</th>
            <th class="text-center">관리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in items" :key="item.adminUserCode">
            <td>{{ index + 1 }}</td>
            <td>
              <strong>{{ item.adminName }}</strong>
              <v-chip v-if="isMe(item)" size="x-small" color="primary" variant="tonal" class="ml-2">본인</v-chip>
            </td>
            <td>{{ item.adminEmail }}</td>
            <td>{{ item.adminPosition || '-' }}</td>
            <td>{{ item.adminPhone || '-' }}</td>
            <td>{{ formatDate(item.createdDt) }}</td>
            <td class="text-center">
              <v-btn size="small" variant="text" icon="mdi-pencil" @click="openEdit(item)" />
              <v-btn size="small" variant="text" icon="mdi-lock-reset" @click="openPassword(item)" />
              <v-btn
                size="small"
                variant="text"
                icon="mdi-delete"
                color="error"
                :disabled="isMe(item)"
                @click="requestRemove(item)"
              />
            </td>
          </tr>
          <tr v-if="!loading && !items.length">
            <td colspan="7" class="text-center py-8 text-grey">등록된 본사관리자가 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="640">
      <v-card rounded="lg" class="dark-dialog">
        <v-card-title>{{ editingCode ? '본사관리자 수정' : '본사관리자 등록' }}</v-card-title>
        <v-card-text>
          <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4">{{ errorMessage }}</v-alert>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.adminName" label="관리자 이름 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.adminEmail" label="이메일 *" variant="outlined" :disabled="!!editingCode" />
            </v-col>
            <v-col v-if="!editingCode" cols="12">
              <v-text-field
                v-model="form.adminPassword"
                label="초기 비밀번호 *"
                type="password"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.adminPosition" label="직책" variant="outlined" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.adminPhone" label="연락처" variant="outlined" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialog = false">취소</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="passwordDialog" max-width="480">
      <v-card rounded="lg" class="dark-dialog">
        <v-card-title>비밀번호 변경</v-card-title>
        <v-card-text>
          <v-alert v-if="passwordErrorMessage" type="error" variant="tonal" class="mb-4">
            {{ passwordErrorMessage }}
          </v-alert>
          <p class="dialog-caption mb-4">{{ passwordTarget?.adminName }} 관리자 비밀번호를 변경합니다.</p>
          <v-text-field
            v-model="passwordForm.newPassword"
            label="새 비밀번호 *"
            type="password"
            variant="outlined"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="passwordDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="saving" @click="savePassword">변경</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="480">
      <v-card rounded="lg" class="dark-dialog">
        <v-card-title>본사관리자 삭제</v-card-title>
        <v-card-text>
          <v-alert v-if="deleteErrorMessage" type="error" variant="tonal" class="mb-4">
            {{ deleteErrorMessage }}
          </v-alert>
          <p>{{ deleteTarget?.adminName }} 본사관리자를 삭제하시겠습니까?</p>
          <p class="dialog-caption mt-2">삭제 후에는 해당 계정으로 관리자 로그인을 할 수 없습니다.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">취소</v-btn>
          <v-btn color="error" :loading="saving" @click="remove">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.head-admin-page {
  color: #eef5ff;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
}

.page-header p {
  margin: 8px 0 0;
  color: #9fb0c5;
}

.search-card {
  margin-bottom: 20px;
}

.search-row {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) auto auto;
  gap: 10px;
  align-items: center;
}

.dark-dialog {
  background: #1f2430;
  color: #eef5ff;
}

.dialog-caption {
  color: #9fb0c5;
}

:deep(.v-card) {
  background-color: #1b202b;
  color: #eef5ff;
  border: 1px solid rgba(148, 163, 184, 0.25);
}

:deep(.v-table) {
  background: transparent;
  color: #eef5ff;
}

:deep(.v-table th) {
  background: #252c39;
  color: #dce8ff;
  font-weight: 700;
}

:deep(.v-table td) {
  border-bottom-color: rgba(148, 163, 184, 0.14);
}

@media (max-width: 720px) {
  .page-header,
  .search-row {
    grid-template-columns: 1fr;
  }

  .page-header {
    display: grid;
  }
}
</style>
